// Packet-walk network simulator.
//
// This is deliberately a *behavioural* model, not a byte-level emulator: it
// answers the questions a junior engineer asks ("does ARP for the gateway
// succeed?", "which route is used?", "does the reply make it back?") using the
// same rules real devices follow, so that show-command output and endpoint
// symptoms are always consistent with the configured state.
//
// Layers modelled: L1 link state and cable faults, L2 VLAN/access/trunk
// flooding with MAC learning, ARP, L3 longest-prefix routing (connected, local,
// static), PAT/NAT overload, DHCP, SLAAC and TCP/UDP service listeners.

import {
  ipToInt, isIp, inSubnet, maskToPrefix, prefixToMask, networkOf, wildcardMatch,
  v6Prefix64, v6Compress, linkLocalFromMac,
} from './addr.js';
import { l2Flood, isSvi, isPo, isSubif, parentIf, sviUp, poStatus, memberFlag } from './l2.js';
import { converge, bindSim, isLoopback } from './routing.js';
import { isSerial, isTunnel, serialStatus, tunnelIfStatus, countTunnel, isPointToPoint } from './wan.js';
import { fhrpOwns, fhrpOwnedIps, relayTarget, logEvent } from './services.js';
import { aclFilter, mgmtListens } from './security.js';

// ----------------------------------------------------------------- lookups
export const dev = (net, id) => net.devices[id];
export const isHost = (d) => d.kind === 'pc' || d.kind === 'server';
/** Devices that route IP: routers, and multilayer switches with `ip routing`. */
export const isL3 = (d) => d.kind === 'router' || (d.kind === 'switch' && !!d.ipRouting);

export function linkOf(net, id, ifc) {
  const phys = ifc && isSubif(ifc) ? parentIf(ifc) : ifc;
  return net.links.find((l) => (l.a.dev === id && l.a.ifc === phys) || (l.b.dev === id && l.b.ifc === phys));
}
export const peerOf = (l, id, ifc) => (l.a.dev === id && l.a.ifc === ifc ? l.b : l.a);

export function ifaceOf(net, id, ifc) {
  const d = dev(net, id);
  return isHost(d) ? d.nic : d.interfaces[ifc];
}
const adminUp = (net, id, ifc) => {
  const d = dev(net, id);
  return isHost(d) ? d.nic.adminUp : !d.interfaces[ifc].shutdown && !d.interfaces[ifc].errDisabled;
};

/** 'up' | 'errors' (up, but corrupting frames) | 'down' */
export function linkState(net, l) {
  if (!l) return 'down';
  const c = l.cable;
  if (!c.seated || !c.compatible) return 'down';
  if (!adminUp(net, l.a.dev, l.a.ifc) || !adminUp(net, l.b.dev, l.b.ifc)) return 'down';
  if (c.damaged || c.length > 100) return 'errors';
  return 'up';
}

/** IOS-style interface status: { status, protocol, connected } */
export function ifStatus(net, id, ifc) {
  const d = dev(net, id);
  const i = ifaceOf(net, id, ifc);
  if (!i) return { status: 'down', protocol: 'down', connected: false };
  if (!isHost(d) && d.sys?.rommon) return { status: 'down', protocol: 'down', connected: false, rommon: true };
  if (!isHost(d) && i.shutdown) return { status: 'administratively down', protocol: 'down', connected: false };
  if (!isHost(d) && i.errDisabled) return { status: 'down', protocol: 'down', connected: false, errDisabled: i.errDisabled };
  if (isLoopback(ifc)) return { status: 'up', protocol: 'up', connected: true, loopback: true };
  if (isTunnel(ifc)) return tunnelIfStatus(net, id, ifc);
  if (d.kind === 'switch' && isSvi(ifc)) {
    return sviUp(net, id, +ifc.slice(4)) ? { status: 'up', protocol: 'up', connected: true } : { status: 'down', protocol: 'down', connected: false };
  }
  if (d.kind === 'switch' && isPo(ifc)) return poStatus(net, id, ifc);
  if (isSubif(ifc)) {
    const p = ifStatus(net, id, parentIf(ifc));
    if (p.status === 'administratively down') return { status: 'down', protocol: 'down', connected: false };
    return p;
  }
  if (d.kind === 'switch' && i.channelGroup && memberFlag(net, id, ifc) === 's') {
    return { status: 'up', protocol: 'down', connected: true, suspended: true };
  }
  const st = linkState(net, linkOf(net, id, ifc));
  if (st !== 'down' && isSerial(ifc)) { const ss = serialStatus(net, id, ifc); if (ss) return ss; }
  return st === 'down'
    ? { status: 'down', protocol: 'down', connected: false }
    : { status: 'up', protocol: 'up', connected: true, errors: st === 'errors' };
}

// ------------------------------------------------------------ host IP stack
/** Effective IPv4 config of a Windows host (static, DHCP lease, or APIPA). */
export function hostIp(d) {
  if (!d.nic.adminUp) return null;
  if (!d.ipcfg.dhcp) return d.ipcfg.ip ? { ip: d.ipcfg.ip, mask: d.ipcfg.mask, gw: d.ipcfg.gw, dns: d.ipcfg.dns } : null;
  if (d.lease) return { ...d.lease };
  if (d.apipa) return { ip: d.apipa, mask: '255.255.0.0', gw: null, dns: [], apipa: true };
  return null;
}
export const hostMediaUp = (net, d) => d.nic.adminUp && linkState(net, linkOf(net, d.id, d.nic.name)) !== 'down';

/** L3 address bound to (device, interface) — or null when the interface cannot carry IP. */
export function l3Addr(net, id, ifc) {
  const d = dev(net, id);
  if (isHost(d)) {
    if (!hostMediaUp(net, d)) return null;
    const c = hostIp(d);
    return c ? { ip: c.ip, mask: c.mask } : null;
  }
  if (d.kind === 'switch' && !isSvi(ifc)) return null;
  if (d.kind !== 'router' && d.kind !== 'switch') return null;
  const i = d.interfaces[ifc];
  if (!i?.ip || ifStatus(net, id, ifc).protocol !== 'up') return null;
  if (isSubif(ifc) && !i.encap) return null;
  return i.ip;
}

export function ownsIp(net, id, ip) {
  const d = dev(net, id);
  if (ip.startsWith('127.')) return true;
  if (isHost(d)) return l3Addr(net, id, d.nic.name)?.ip === ip;
  if (d.kind !== 'router' && d.kind !== 'switch') return false;
  if (Object.keys(d.interfaces).some((n) => l3Addr(net, id, n)?.ip === ip)) return true;
  return d.kind === 'router' && fhrpOwnedIps(net, id).includes(ip);
}

// ------------------------------------------------------------------ Layer 2
// VLAN flooding, trunks, EtherChannel and STP live in ./l2.js.
export { l2Flood };

function learnMac(net, swId, vlan, mac, port) {
  if (!port) return; // frame originated/terminated at the switch's own SVI
  const s = dev(net, swId);
  const e = s.macTable.find((x) => x.mac === mac && x.vlan === vlan);
  if (e) e.port = port; else s.macTable.push({ vlan, mac, port, type: 'DYNAMIC' });
}
function macOf(net, id, ifc) {
  const d = dev(net, id);
  return isHost(d) ? d.nic.mac : d.interfaces[ifc].mac;
}
function bumpErrors(net, linkIds) {
  for (const lid of linkIds) {
    const l = net.links.find((x) => x.id === lid);
    for (const end of [l.a, l.b]) {
      const i = ifaceOf(net, end.dev, end.ifc);
      if (i?.counters) { i.counters.crc += 37; i.counters.inErrors += 41; }
    }
  }
}
function countFrames(net, hops) {
  for (const h of hops) {
    const s = dev(net, h.sw);
    if (h.inPort && s.interfaces[h.inPort]?.counters) s.interfaces[h.inPort].counters.inPkts++;
    if (h.outPort && s.interfaces[h.outPort]?.counters) s.interfaces[h.outPort].counters.outPkts++;
  }
}

/**
 * ARP for `targetIp` out of (id, ifc). Performs MAC learning on every switch the
 * broadcast reaches and on the reply path, and fills both ARP caches.
 */
export function arpResolve(net, id, ifc, targetIp) {
  const src = l3Addr(net, id, ifc);
  if (!src) return { ok: false };
  const d = dev(net, id);
  const srcMac = macOf(net, id, ifc);
  const reached = l2Flood(net, id, ifc);
  for (const r of reached) for (const h of r.hops) learnMac(net, h.sw, h.vlan, srcMac, h.inPort);
  // Point-to-point links (serial, tunnels) have exactly one neighbour and no ARP:
  // anything routed out of them is delivered to the far end.
  const hit = isPointToPoint(ifc)
    ? reached.find((r) => l3Addr(net, r.dev, r.ifc))
    : reached.find((r) => l3Addr(net, r.dev, r.ifc)?.ip === targetIp || (dev(net, r.dev).kind === 'router' && fhrpOwns(net, r.dev, r.ifc, targetIp, srcMac)));
  if (!hit) return { ok: false, reached };
  if (isPointToPoint(ifc)) return { ok: true, target: hit, lossy: false, hadEntry: true };
  // Two first-hop-redundancy routers both Active for the same virtual IP (e.g. HSRP
  // group mismatch): ARP replies alternate between them, so forwarding is intermittent.
  const dupVip = reached.filter((r) => dev(net, r.dev).kind === 'router' && fhrpOwns(net, r.dev, r.ifc, targetIp, srcMac)).length > 1;
  const tMac = macOf(net, hit.dev, hit.ifc);
  for (const h of hit.hops) learnMac(net, h.sw, h.vlan, tMac, h.outPort);
  countFrames(net, hit.hops);
  bumpErrors(net, hit.errLinks);
  const cache = (x) => (isHost(x) ? x.arp : x.arp);
  const hadEntry = !!cache(d)[targetIp];
  cache(d)[targetIp] = { mac: tMac, ifc };
  const t = dev(net, hit.dev);
  cache(t)[src.ip] = { mac: srcMac, ifc: hit.ifc };
  return { ok: true, target: hit, lossy: hit.errLinks.length > 0 || dupVip, hadEntry };
}

// ------------------------------------------------------------------ Layer 3
/**
 * Full IOS routing table: connected, local, static (incl. floating and Null0),
 * RIP, OSPF, EIGRP and BGP — see ./routing.js. Best administrative distance per
 * prefix wins; equal-cost paths of the same protocol are all kept.
 */
export function routingTable(net, id) {
  return converge(net).rib[id] || [];
}

/** Longest-prefix match. Returns { ifc, nh } or null. */
export function routeLookup(net, id, dst) {
  const d = dev(net, id);
  if (isHost(d)) {
    if (!hostMediaUp(net, d)) return null;
    const c = hostIp(d);
    if (!c) return null;
    if (inSubnet(dst, c.ip, c.mask)) return { ifc: d.nic.name, nh: null };
    if (c.gw && inSubnet(c.gw, c.ip, c.mask)) return { ifc: d.nic.name, nh: c.gw };
    return null;
  }
  if (d.kind === 'switch' && !d.ipRouting) {
    // Layer 2 switch: management SVI + ip default-gateway, like a host.
    for (const n of Object.keys(d.interfaces).filter(isSvi)) {
      const a = l3Addr(net, id, n);
      if (!a) continue;
      if (inSubnet(dst, a.ip, a.mask)) return { ifc: n, nh: null };
      if (d.defaultGateway && inSubnet(d.defaultGateway, a.ip, a.mask)) return { ifc: n, nh: d.defaultGateway };
    }
    return null;
  }
  if (!isL3(d)) return null;
  let best = null;
  for (const r of routingTable(net, id)) {
    if (!inSubnet(dst, r.net, r.prefix)) continue;
    if (!best || r.prefix > best.prefix) best = r;
  }
  if (!best) return null;
  return { ifc: best.ifc, nh: best.code === 'C' || best.code === 'L' || best.exitOnly ? null : best.nh, route: best };
}

// ---------------------------------------------------------------------- NAT
// Inside source NAT: static one-to-one, dynamic pool (one-to-one or overload) and
// PAT on the outside interface address. State: r.nat.translations (recent flows),
// r.nat.natBindings (dynamic one-to-one bindings), r.nat.natStats (hits / misses).
const ipInt = (x) => ipToInt(x) >>> 0;
const intIp = (n) => [n >>> 24, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
function natRecord(r, pkt, global, type, extra = {}) {
  const t = { proto: pkt.proto, insideLocal: pkt.src, insideGlobal: global, outside: pkt.dst, flow: pkt.flow, port: pkt.dport || 0, lport: type === 'pat' ? 1024 + (r.nat.translations.length % 60000) : null, type, ...extra };
  r.nat.translations.push(t);
  if (r.nat.translations.length > 40) r.nat.translations.shift();
  const st = (r.nat.natStats ||= { hits: 0, misses: 0 });
  st.hits++;
  return global;
}
function natOutside(net, r, inIfc, outIfc, pkt) {
  const ii = r.interfaces[inIfc], oi = r.interfaces[outIfc];
  if (ii?.nat !== 'inside' || oi?.nat !== 'outside') return null;
  const st = (r.nat.natStats ||= { hits: 0, misses: 0 });
  const stat = (r.nat.statics || []).find((x) => x.local === pkt.src);
  if (stat) return natRecord(r, pkt, stat.global, 'static');
  for (const rule of r.nat.rules) {
    const acl = r.acls[rule.acl];
    if (!acl || !aclPermits(acl, pkt.src)) continue;
    if (rule.ifc) {
      if (rule.ifc !== outIfc) continue;
      const g = r.interfaces[outIfc].ip?.ip;
      if (!g) continue;
      return natRecord(r, pkt, g, 'pat');
    }
    const pool = r.nat.pools?.[rule.pool];
    if (!pool) { st.misses++; return null; }
    if (rule.overload) return natRecord(r, pkt, pool.start, 'pat', { pool: rule.pool });
    const b = (r.nat.natBindings ||= {});
    if (b[pkt.src]) return natRecord(r, pkt, b[pkt.src], 'dynamic', { pool: rule.pool });
    const used = new Set(Object.values(b));
    for (let n = ipInt(pool.start); n <= ipInt(pool.end); n++) {
      const g = intIp(n);
      if (used.has(g) || (r.nat.statics || []).some((x) => x.global === g)) continue;
      b[pkt.src] = g;
      return natRecord(r, pkt, g, 'dynamic', { pool: rule.pool });
    }
    st.misses++; // pool exhausted: the packet is dropped
    return false;
  }
  return null;
}
/** Outside → inside: existing flow, static mapping or dynamic binding. */
function natInbound(r, pkt) {
  const t = r.nat.translations.find((x) => x.insideGlobal === pkt.dst && x.flow === pkt.flow);
  if (t) return t.insideLocal;
  const stat = (r.nat.statics || []).find((x) => x.global === pkt.dst);
  if (stat) return stat.local;
  const b = Object.entries(r.nat.natBindings || {}).find(([, g]) => g === pkt.dst);
  return b ? b[0] : null;
}
export function aclPermits(acl, ip) {
  for (const e of acl) {
    if (wildcardMatch(ip, e.base, e.wild)) return e.action === 'permit';
  }
  return false; // implicit deny
}

// ---------------------------------------------------------------- forwarding
/**
 * Walk a packet hop by hop. Mutates ARP/MAC/NAT state like real traffic would.
 * Returns { ok, at, pkt, path, lossy, reason?, inIfc? }
 *   reason: 'no-route' | 'arp' | 'ttl' | 'host-drop' | 'loop'
 */
export function forward(net, fromId, pkt0) {
  const pkt = { ttl: 64, ...pkt0 };
  let cur = fromId, inIfc = null, lossy = false;
  const path = [];
  for (let guard = 0; guard < 40; guard++) {
    const d = dev(net, cur);
    // Inbound interface ACL (also filters traffic addressed to the router itself).
    if (d.kind === 'router' && inIfc && !aclFilter(net, cur, inIfc, 'in', pkt, logEvent)) return { ok: false, reason: 'acl', at: cur, pkt, path, lossy, inIfc, aclDir: 'in' };
    // Un-NAT replies arriving on an outside interface.
    if (d.kind === 'router' && inIfc && d.interfaces[inIfc]?.nat === 'outside') {
      const local = natInbound(d, pkt);
      if (local) pkt.dst = local;
    }
    if (ownsIp(net, cur, pkt.dst)) return { ok: true, at: cur, pkt, path, lossy, inIfc };
    if (cur !== fromId) {
      if (isHost(d) || !isL3(d)) return { ok: false, reason: 'host-drop', at: cur, pkt, path, lossy, inIfc };
      pkt.ttl--;
      if (pkt.ttl <= 0) return { ok: false, reason: 'ttl', at: cur, pkt, path, lossy, inIfc };
    }
    const r = routeLookup(net, cur, pkt.dst);
    if (!r || r.ifc === 'Null0') return { ok: false, reason: 'no-route', at: cur, pkt, path, lossy, inIfc };
    if (d.kind === 'router' && inIfc && !pkt.natted) {
      const g = natOutside(net, d, inIfc, r.ifc, pkt);
      if (g === false) return { ok: false, reason: 'no-route', at: cur, pkt, path, lossy, inIfc }; // NAT pool exhausted
      if (g) { pkt.src = g; pkt.natted = true; }
    }
    // Outbound interface ACL (never applied to traffic the router originates).
    if (d.kind === 'router' && cur !== fromId && !aclFilter(net, cur, r.ifc, 'out', pkt, logEvent)) return { ok: false, reason: 'acl', at: cur, pkt, path, lossy, inIfc, outIfc: r.ifc, aclDir: 'out' };
    const nh = r.nh || pkt.dst;
    const a = arpResolve(net, cur, r.ifc, nh);
    if (!a.ok) return { ok: false, reason: 'arp', at: cur, pkt, path, lossy, outIfc: r.ifc, nh, inIfc };
    if (a.lossy) lossy = true;
    if (isTunnel(r.ifc)) countTunnel(net, cur, r.ifc, a.target);
    if (!a.hadEntry && !isHost(d)) pkt.firstArp = true;
    path.push({ dev: cur, out: r.ifc, nh, in: inIfc });
    inIfc = a.target.ifc;
    cur = a.target.dev;
  }
  return { ok: false, reason: 'loop', at: cur, pkt, path, lossy };
}

/** Source IP a device uses to reach dst (host IP, or router egress interface). */
export function sourceIp(net, id, dst) {
  const d = dev(net, id);
  if (isHost(d)) return hostIp(d)?.ip || null;
  const r = routeLookup(net, id, dst);
  return r ? l3Addr(net, id, r.ifc)?.ip : null;
}

function listens(d, proto, port) {
  if (!isHost(d)) return (d.kind === 'router' || d.kind === 'switch') && mgmtListens(d, proto, port);
  // A lab AAA server's RADIUS / TACACS+ service can be stopped (Stop-Service).
  if (d.aaa && ((port === 49 && d.aaa.tacacs?.enabled === false) || ((port === 1812 || port === 1813) && d.aaa.radius?.enabled === false))) return false;
  if (d.tftp && proto === 'udp' && port === 69) return d.tftp.enabled !== false;
  return d.services.some((s) => s.proto === proto && s.port === port);
}

let flowSeq = 1;
/**
 * Request + reply. For ICMP this is ping; for tcp/udp it checks the service.
 * Returns { ok, stage, fwd, back, target, hops, lossy, unreachableFrom? }
 */
export function roundTrip(net, srcId, dst, { proto = 'icmp', port = 0, ttl = 64, src: srcOverride = null } = {}) {
  const src = srcOverride || sourceIp(net, srcId, dst);
  if (!src) return { ok: false, stage: 'no-source' };
  const flow = flowSeq++;
  const sport = proto === 'icmp' ? undefined : 49152 + (flow % 16000);
  const fwd = forward(net, srcId, { src, dst, proto, dport: port, sport, icmp: proto === 'icmp' ? 'echo' : undefined, flow, ttl });
  if (!fwd.ok) {
    // A router that has no route (or whose ACL denied the packet) sends ICMP
    // "unreachable" back (administratively prohibited for an ACL).
    let unreachableFrom = null;
    if ((fwd.reason === 'no-route' || fwd.reason === 'ttl' || fwd.reason === 'acl') && fwd.at !== srcId && isL3(dev(net, fwd.at))) {
      const from = fwd.inIfc ? l3Addr(net, fwd.at, fwd.inIfc)?.ip : null;
      if (from) {
        const back = forward(net, fwd.at, { src: from, dst: fwd.pkt.src, proto: 'icmp', icmp: 'unreachable', flow, ttl: 255 });
        if (back.ok) unreachableFrom = from;
      }
    }
    return { ok: false, stage: 'forward', fwd, unreachableFrom, prohibited: fwd.reason === 'acl' };
  }
  const target = dev(net, fwd.at);
  if (proto !== 'icmp' && !listens(target, proto, port)) {
    return { ok: false, stage: 'refused', fwd, target };
  }
  const back = forward(net, fwd.at, { src: fwd.pkt.dst, dst: fwd.pkt.src, proto, sport: proto === 'icmp' ? undefined : port, dport: sport, icmp: proto === 'icmp' ? 'echo-reply' : undefined, reply: true, flow, ttl: target.ttl || 64 });
  if (!back.ok) return { ok: false, stage: 'return', fwd, back, target, prohibited: back.reason === 'acl' };
  const routersBack = back.path.filter((h) => h.dev !== fwd.at).length;
  return {
    ok: true, fwd, back, target, lossy: fwd.lossy || back.lossy,
    replyTtl: (target.ttl || 64) - routersBack,
    hops: fwd.path.length, firstArp: fwd.pkt.firstArp || back.pkt.firstArp,
  };
}

/** Traceroute: returns per-hop results { ip|null, final, unreachable }. */
export function trace(net, srcId, dst, maxHops = 30) {
  const hops = [];
  for (let t = 1; t <= maxHops; t++) {
    const src = sourceIp(net, srcId, dst);
    if (!src) { hops.push({ ip: null, fail: true }); break; }
    const flow = flowSeq++;
    const fwd = forward(net, srcId, { src, dst, proto: 'icmp', icmp: 'echo', flow, ttl: t });
    if (fwd.ok) {
      const back = forward(net, fwd.at, { src: fwd.pkt.dst, dst: fwd.pkt.src, proto: 'icmp', icmp: 'echo-reply', reply: true, flow, ttl: 255 });
      hops.push(back.ok ? { ip: dst, final: true } : { ip: null });
      if (back.ok) break;
      continue;
    }
    const replier = fwd.at !== srcId && isL3(dev(net, fwd.at))
      ? l3Addr(net, fwd.at, fwd.inIfc)?.ip : null;
    if (fwd.reason === 'ttl' || fwd.reason === 'no-route' || fwd.reason === 'acl') {
      let ok = false;
      const stop = fwd.reason !== 'ttl';
      if (replier) ok = forward(net, fwd.at, { src: replier, dst: fwd.pkt.src, proto: 'icmp', icmp: stop ? 'unreachable' : 'time-exceeded', flow, ttl: 255 }).ok;
      hops.push({ ip: ok ? replier : null, unreachable: stop && ok, prohibited: fwd.reason === 'acl' && ok });
      if (stop && ok) break;
      if (stop && fwd.at === srcId) break;
    } else {
      hops.push({ ip: null });
    }
  }
  return hops;
}

// --------------------------------------------------------------------- DNS
/** Resolve a name via the host's configured DNS server(s). */
export function dnsQuery(net, hostId, name, serverIp) {
  const d = dev(net, hostId);
  const servers = serverIp ? [serverIp] : (hostIp(d)?.dns || []);
  if (!servers.length) return { ok: false, reason: 'no-server' };
  const srv = servers[0];
  const rt = roundTrip(net, hostId, srv, { proto: 'udp', port: 53 });
  if (!rt.ok) return { ok: false, reason: rt.stage === 'refused' ? 'refused' : 'timeout', server: srv };
  const recs = rt.target.dnsRecords || {};
  const key = name.toLowerCase().replace(/\.$/, '');
  // Single-label names get the connection's DNS suffix appended (DHCP option 15 / static suffix).
  const suffix = hostIp(d)?.domain || d.ipcfg?.dnsSuffix || null;
  const ans = recs[key] ?? (!key.includes('.') && suffix ? recs[`${key}.${suffix}`] : undefined);
  return ans ? { ok: true, server: srv, serverName: rt.target.dnsName || 'UnKnown', address: ans, authoritative: !!rt.target.authoritative }
    : { ok: false, reason: 'nxdomain', server: srv, serverName: rt.target.dnsName || 'UnKnown' };
}

// --------------------------------------------------------------------- DHCP
/** DHCP DISCOVER → OFFER. Serves from any router interface in the broadcast domain. */
export function dhcpObtain(net, hostId) {
  const d = dev(net, hostId);
  if (!hostMediaUp(net, d)) return { ok: false, reason: 'media' };
  const reached = l2Flood(net, hostId, d.nic.name);
  const learn = (r) => { for (const h of r.hops) learnMac(net, h.sw, h.vlan, d.nic.mac, h.inPort); };
  // 1) a DHCP server on the segment itself (IOS DHCP server on the gateway)
  for (const r of reached) {
    const rd = dev(net, r.dev);
    if (rd.kind !== 'router') continue;
    const gwAddr = l3Addr(net, r.dev, r.ifc);
    if (!gwAddr) continue;
    for (const pool of Object.values(rd.dhcp.pools)) {
      if (!pool.network || !inSubnet(gwAddr.ip, pool.network, pool.mask)) continue;
      const ip = leaseFrom(rd, pool, gwAddr.ip, d.nic.mac);
      if (!ip) continue;
      learn(r);
      return { ok: true, lease: { ip, mask: pool.mask, gw: pool.router || null, dns: pool.dns || [], domain: pool.domain || null, server: gwAddr.ip } };
    }
  }
  // 2) relayed: a router interface with ip helper-address forwards to a remote server
  for (const r of reached) {
    const rd = dev(net, r.dev);
    if (rd.kind !== 'router' || !rd.interfaces[r.ifc]?.helper) continue;
    const t = relayTarget(net, r.dev, r.ifc);
    if (!t?.server) continue;
    const sd = dev(net, t.server.dev);
    for (const pool of Object.values(sd.dhcp.pools)) {
      if (!pool.network || !inSubnet(t.giaddr, pool.network, pool.mask)) continue;
      const ip = leaseFrom(sd, pool, t.giaddr, d.nic.mac);
      if (!ip) continue;
      learn(r);
      return { ok: true, lease: { ip, mask: pool.mask, gw: pool.router || null, dns: pool.dns || [], domain: pool.domain || null, server: t.server.ip, relay: t.giaddr } };
    }
  }
  return { ok: false, reason: 'timeout' };
}
/** Pick (or re-use) an address for `mac` from an IOS DHCP pool. */
function leaseFrom(rd, pool, avoid, mac) {
  const existing = rd.dhcp.bindings.find((b) => b.mac === mac && b.pool === pool.name);
  if (existing && inSubnet(existing.ip, pool.network, pool.mask)) return existing.ip;
  rd.dhcp.bindings = rd.dhcp.bindings.filter((b) => b.mac !== mac);
  const base = ipToInt(networkOf(pool.network, pool.mask));
  const size = 2 ** (32 - maskToPrefix(pool.mask));
  for (let n = 1; n < size - 1; n++) {
    const x = base + n;
    const cand = [x >>> 24, (x >>> 16) & 255, (x >>> 8) & 255, x & 255].join('.');
    if (cand === avoid) continue;
    if (rd.dhcp.excluded.some(([a, b]) => ipToInt(cand) >= ipToInt(a) && ipToInt(cand) <= ipToInt(b || a))) continue;
    if (rd.dhcp.bindings.some((b) => b.ip === cand)) continue;
    rd.dhcp.bindings.push({ ip: cand, mac, pool: pool.name });
    return cand;
  }
  return null;
}
export function apipaFor(mac) {
  const h = mac.replace(/[^0-9a-f]/gi, '');
  return `169.254.${parseInt(h.slice(8, 10), 16) || 1}.${parseInt(h.slice(10, 12), 16) || 1}`;
}

// ------------------------------------------------------------------- IPv6
/** SLAAC: hosts learn a global /64 from any IPv6 router in their broadcast domain. */
export function slaac(net, hostId) {
  const d = dev(net, hostId);
  const addrs = [{ addr: linkLocalFromMac(d.nic.mac), type: 'link-local' }];
  let gw = null;
  if (hostMediaUp(net, d)) {
    for (const r of l2Flood(net, hostId, d.nic.name)) {
      const rd = dev(net, r.dev);
      if (rd.kind !== 'router' || !rd.ipv6Routing) continue;
      const i = rd.interfaces[r.ifc];
      if (ifStatus(net, r.dev, r.ifc).protocol !== 'up') continue;
      const g = i.ipv6.find((a) => a.prefix === 64 && !a.addr.startsWith('fe80'));
      if (!g) continue;
      const iid = d.nic.mac.replace(/[^0-9a-f]/gi, '').slice(4);
      const tail = `${iid.slice(0, 4)}:${iid.slice(4)}:${d.id.length}a1:${(parseInt(iid.slice(-2), 16) * 7).toString(16)}`;
      addrs.unshift({ addr: v6Compress(`${v6Prefix64(g.addr)}:${tail}`), type: 'global' });
      gw = routerLinkLocal(rd, r.ifc);
      break;
    }
  }
  return { addrs, gw };
}
export function routerLinkLocal(rd, ifc) {
  const i = rd.interfaces[ifc];
  return i.ipv6.find((a) => a.addr.startsWith('fe80'))?.addr || linkLocalFromMac(i.mac);
}
/** IPv6 ping on-link only (sufficient for Domain 01). */
export function ping6(net, hostId, target) {
  const d = dev(net, hostId);
  if (!hostMediaUp(net, d)) return false;
  const t = v6Compress(target);
  if (!t) return false;
  // Without a global address (no RA received) the host has no route to a global destination.
  if (!t.startsWith('fe80') && !slaac(net, hostId).addrs.some((a) => a.type === 'global')) return false;
  for (const r of l2Flood(net, hostId, d.nic.name)) {
    const rd = dev(net, r.dev);
    if (rd.kind === 'router') {
      const i = rd.interfaces[r.ifc];
      if (!i.ipv6Enabled && !i.ipv6.length) continue;
      if (ifStatus(net, r.dev, r.ifc).protocol !== 'up') continue;
      const own = [...i.ipv6.map((a) => v6Compress(a.addr)), routerLinkLocal(rd, r.ifc)];
      if (own.includes(t)) return true;
    } else if (isHost(rd)) {
      if (slaac(net, r.dev).addrs.some((a) => a.addr === t)) return true;
    }
  }
  return false;
}

/** Utility for labs: does the device list reach the given IP end-to-end? */
bindSim({ l3Addr, ifStatus });

export const probeReach = (net, srcId, dst, opts) => roundTrip(JSON.parse(JSON.stringify(net)), srcId, dst, opts).ok;
export { prefixToMask, isIp };

/**
 * Reachability for a device's own management traffic (AAA, TFTP...): a request
 * and its reply on a copy of the network, optionally sourced from a configured
 * interface. Returns { ok, src }.
 */
export function mgmtReach(net, id, ip, { proto = 'udp', port = 0, srcIf = null } = {}) {
  const src = srcIf ? l3Addr(net, id, srcIf)?.ip : sourceIp(net, id, ip);
  if (!src) return { ok: false, src: null };
  return { ok: roundTrip(JSON.parse(JSON.stringify(net)), id, ip, { proto, port, src }).ok, src };
}
